import { Rng } from '@/core/rng';
import { TRAPS, type TrapDefinition, trapDensityFor, trapTableFor } from '@/data/traps';
import { type Floor, type Room, isWalkable } from '@/dungeon/generator';

/**
 * Traps on a floor.
 *
 * A layer over the map, like chests, so the terrain stays exactly what its
 * seed says. Placed from a stream salted apart from rooms, chests and loot —
 * adding traps to a floor cannot move a wall or change what is in a box.
 *
 * **Corridors only.** A trap in a room is a tax on being in the room; a trap
 * in a corridor is a question about which way round. Rooms are where fighting
 * happens and where chests are, and both are worse if the floor is also
 * biting.
 */

const TRAP_SALT = 0x4;

export interface Trap {
  readonly x: number;
  readonly y: number;
  readonly definition: TrapDefinition;
  /** Seconds until it can catch you again; 0 means armed. */
  cooldown: number;
}

function insideAnyRoom(rooms: readonly Room[], x: number, y: number): boolean {
  return rooms.some(
    (room) =>
      x >= room.x && x < room.x + room.width && y >= room.y && y < room.y + room.height,
  );
}

/**
 * Walkable tiles that belong to no room.
 *
 * Exported because "which tiles are corridor" is a question about a floor that
 * traps happen to be the first to ask, and it is far easier to test directly
 * than through whatever ends up standing on them.
 */
export function corridorTiles(floor: Floor): { x: number; y: number }[] {
  const tiles: { x: number; y: number }[] = [];

  for (let y = 0; y < floor.rows.length; y++) {
    const row = floor.rows[y]!;
    for (let x = 0; x < row.length; x++) {
      if (!isWalkable(row[x])) continue;
      if (insideAnyRoom(floor.rooms, x, y)) continue;
      tiles.push({ x, y });
    }
  }

  return tiles;
}

export class TrapField {
  private readonly byTile = new Map<string, Trap>();

  static forFloor(seed: number, floor: Floor): TrapField {
    const field = new TrapField();
    const corridors = corridorTiles(floor);
    if (corridors.length === 0) return field;

    const rng = Rng.forStream(seed, floor.depth, TRAP_SALT);
    const table = trapTableFor(floor.depth);
    const wanted = Math.floor(corridors.length / trapDensityFor(floor.depth));

    for (const tile of rng.shuffle(corridors).slice(0, wanted)) {
      const id = rng.pick(table);
      if (!id) continue;
      field.byTile.set(`${tile.x},${tile.y}`, {
        x: tile.x,
        y: tile.y,
        definition: TRAPS[id],
        cooldown: 0,
      });
    }

    return field;
  }

  all(): Trap[] {
    return [...this.byTile.values()];
  }

  at(tileX: number, tileY: number): Trap | null {
    return this.byTile.get(`${tileX},${tileY}`) ?? null;
  }

  update(dt: number): void {
    for (const trap of this.byTile.values()) {
      trap.cooldown = Math.max(0, trap.cooldown - dt);
    }
  }

  /**
   * Step onto a tile. Returns the damage taken, or 0.
   *
   * Sprung traps rearm on a timer rather than staying open, so a corridor
   * crossed once is not permanently safe — but crossing back immediately,
   * which is usually a retreat, is.
   */
  trigger(tileX: number, tileY: number): number {
    const trap = this.at(tileX, tileY);
    if (!trap || trap.cooldown > 0) return 0;

    trap.cooldown = trap.definition.rearmSeconds;
    return trap.definition.damage;
  }
}
